import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import {
  Plus, Trash2, CheckCircle, AlertCircle, X,
  Search, ShoppingCart, ChevronDown, ChevronUp
} from "lucide-react";

const STATUS_CFG = {
  draft:     { cls: "bg-slate-100 text-slate-500",    label: "Draft" },
  sent:      { cls: "bg-blue-100 text-blue-700",      label: "Sent" },
  confirmed: { cls: "bg-violet-100 text-violet-700",  label: "Confirmed" },
  received:  { cls: "bg-emerald-100 text-emerald-700",label: "Received" },
  cancelled: { cls: "bg-red-100 text-red-600",        label: "Cancelled" },
};

const STATUS_FLOW = ["draft", "sent", "confirmed", "received", "cancelled"];

const EMPTY_ITEM = { description: "", qty: 1, unitPrice: 0, unit: "unit" };

const fmt = (n) => `A$${Number(n || 0).toFixed(2)}`;

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

export default function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    supplierId: "", supplierName: "", currency: "AUD",
    expectedDelivery: "", notes: "",
    items: [{ ...EMPTY_ITEM }],
  });

  const flash = (msg, type = "success") => {
    if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    else { setError(msg); setTimeout(() => setError(""), 5000); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [poRes, supRes] = await Promise.all([
        api.get("/api/purchase-orders"),
        api.get("/api/suppliers"),
      ]);
      setOrders(poRes.data.orders || []);
      setSuppliers((supRes.data.suppliers || []).filter(s => s.status === "active"));
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(orders.filter(o =>
      o.poNumber?.toLowerCase().includes(q) ||
      o.supplierName?.toLowerCase().includes(q) ||
      o.status?.toLowerCase().includes(q)
    ));
  }, [search, orders]);

  const closeModal = () => { setModal(null); setError(""); };

  const openCreate = () => {
    setForm({ supplierId: "", supplierName: "", currency: "AUD", expectedDelivery: "", notes: "", items: [{ ...EMPTY_ITEM }] });
    setModal("create");
  };

  const setItem = (idx, field, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: val };
      return { ...f, items };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.unitPrice) || 0), 0);

  const handleCreate = async () => {
    if (!form.supplierId) { flash("Select a supplier", "error"); return; }
    if (form.items.some(i => !i.description.trim())) { flash("All items need a description", "error"); return; }
    setSaving(true);
    try {
      await api.post("/api/purchase-orders", form);
      flash("Purchase order created.");
      closeModal();
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.patch(`/api/purchase-orders/${id}/status`, { status });
      flash(`PO marked as ${status}.`);
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this draft purchase order?")) return;
    try {
      await api.delete(`/api/purchase-orders/${id}`);
      flash("Purchase order deleted.");
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const supplierSelect = (e) => {
    const sup = suppliers.find(s => s.id === e.target.value);
    setForm(f => ({ ...f, supplierId: sup?.id || "", supplierName: sup?.name || "", currency: sup?.currency || "AUD" }));
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-violet-100 rounded-xl"><ShoppingCart size={24} className="text-violet-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900">Purchase Orders</h1>
            <p className="text-slate-500 text-sm">Raise and track POs for your suppliers</p>
          </div>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 transition-colors">
          <Plus size={16}/> New PO
        </button>
      </div>

      {/* Alerts */}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {STATUS_FLOW.map(s => {
          const cfg = STATUS_CFG[s];
          const count = orders.filter(o => o.status === s).length;
          return (
            <div key={s} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{cfg.label}</p>
              <p className="text-2xl font-bold text-navy-900">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-3">
        <Search size={16} className="text-slate-400"/>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by PO number, supplier, or status..."
          className="flex-1 text-sm focus:outline-none text-slate-700 placeholder-slate-400"/>
        {search && <button onClick={() => setSearch("")}><X size={15} className="text-slate-400"/></button>}
      </div>

      {/* PO List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">
            {search ? "No purchase orders match your search." : "No purchase orders yet. Create your first one."}
          </div>
        ) : filtered.map(po => (
          <div key={po.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <button onClick={() => setExpanded(expanded === po.id ? null : po.id)}
                  className="text-slate-400 hover:text-slate-600">
                  {expanded === po.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                </button>
                <div>
                  <p className="font-bold text-navy-900 font-mono">{po.poNumber}</p>
                  <p className="text-xs text-slate-400">{po.supplierName}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-bold text-navy-900">{fmt(po.subtotal)} {po.currency}</span>
                <StatusBadge status={po.status}/>
                <select
                  value={po.status}
                  onChange={e => handleStatusChange(po.id, e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-300"
                >
                  {STATUS_FLOW.map(s => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
                </select>
                {po.status === "draft" && (
                  <button onClick={() => handleDelete(po.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            </div>

            {/* Expanded line items */}
            {expanded === po.id && (
              <div className="border-t border-slate-100 px-6 py-4 bg-slate-50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="text-left pb-2">Description</th>
                      <th className="text-right pb-2">Qty</th>
                      <th className="text-right pb-2">Unit</th>
                      <th className="text-right pb-2">Unit Price</th>
                      <th className="text-right pb-2">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(po.items || []).map((item, i) => (
                      <tr key={i}>
                        <td className="py-2 text-slate-700">{item.description}</td>
                        <td className="py-2 text-right text-slate-600">{item.qty}</td>
                        <td className="py-2 text-right text-slate-600">{item.unit}</td>
                        <td className="py-2 text-right text-slate-600">{fmt(item.unitPrice)}</td>
                        <td className="py-2 text-right font-semibold text-navy-900">{fmt(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200">
                      <td colSpan={4} className="pt-3 text-right font-bold text-navy-900 text-sm">Total</td>
                      <td className="pt-3 text-right font-bold text-navy-900">{fmt(po.subtotal)}</td>
                    </tr>
                  </tfoot>
                </table>
                {po.notes && <p className="mt-3 text-xs text-slate-500"><span className="font-semibold">Notes:</span> {po.notes}</p>}
                {po.expectedDelivery && <p className="mt-1 text-xs text-slate-500"><span className="font-semibold">Expected delivery:</span> {po.expectedDelivery}</p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {modal === "create" && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="font-bold text-navy-900 text-lg">New Purchase Order</h2>
              <button onClick={closeModal}><X size={18} className="text-slate-400"/></button>
            </div>

            <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
              {error && <p className="text-xs text-red-500">{error}</p>}

              {/* Supplier */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Supplier *</label>
                <select value={form.supplierId} onChange={supplierSelect}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                  <option value="">— Select a supplier —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                </select>
                {suppliers.length === 0 && <p className="text-xs text-amber-500 mt-1">No active suppliers. Add one in the Suppliers page first.</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Expected Delivery</label>
                  <input type="date" value={form.expectedDelivery} onChange={e => setForm(f => ({...f, expectedDelivery: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Currency</label>
                  <input value={form.currency} readOnly
                    className="mt-1 w-full border border-slate-100 bg-slate-50 rounded-xl px-4 py-3 text-sm text-slate-500"/>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Line Items</label>
                  <button onClick={addItem} className="text-xs text-violet-600 font-bold flex items-center gap-1 hover:text-violet-800">
                    <Plus size={12}/> Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input value={item.description} onChange={e => setItem(idx, "description", e.target.value)}
                        placeholder="Description" className="col-span-5 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <input type="number" value={item.qty} min="1" onChange={e => setItem(idx, "qty", e.target.value)}
                        placeholder="Qty" className="col-span-2 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <input value={item.unit} onChange={e => setItem(idx, "unit", e.target.value)}
                        placeholder="Unit" className="col-span-2 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <input type="number" value={item.unitPrice} min="0" step="0.01" onChange={e => setItem(idx, "unitPrice", e.target.value)}
                        placeholder="Price" className="col-span-2 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <button onClick={() => removeItem(idx)} disabled={form.items.length === 1}
                        className="col-span-1 p-2 text-slate-400 hover:text-red-500 disabled:opacity-30">
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-3 font-bold text-navy-900">Total: {fmt(subtotal)}</div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                  rows={2} placeholder="Optional notes..."
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"/>
              </div>
            </div>

            <div className="px-6 pb-6 pt-4 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={closeModal} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreate} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-navy-900 text-white text-sm font-bold hover:bg-navy-800 disabled:opacity-50">
                {saving ? "Creating..." : "Create PO"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
